// mindmetal-app/web/src/components/mindmetal/DistillationPanel.tsx
import { useState } from 'react'
import { motion } from 'framer-motion'
import { Beaker, FileText, MessageSquare, Mic } from 'lucide-react'
import { Button, Input, Textarea, Card, CardHeader, CardTitle, CardContent } from '@personic/shared'
import { useRunDistillation } from '@/hooks/useMindMetaLTestUsers'
import { SignalDiffViewer } from './SignalDiffViewer'
import type { DistillationRequest, DistillationResult } from '@personic/shared'

interface DistillationPanelProps {
  canonicalId: string
  onSuccess?: (result: DistillationResult) => void
}

const CONTENT_TYPES = [
  { value: 'questionnaire', label: 'Questionnaire (Q&A format)', icon: FileText },
  { value: 'monolog', label: 'Monolog (spoken text)', icon: Mic },
  { value: 'transcript', label: 'Transcript (conversation)', icon: MessageSquare },
] as const

export function DistillationPanel({ canonicalId, onSuccess }: DistillationPanelProps) {
  const [content, setContent] = useState('')
  const [contentType, setContentType] = useState<DistillationRequest['content_type']>('questionnaire')
  const [context, setContext] = useState('')
  const [tiers, setTiers] = useState<('universal' | 'behavioral')[]>(['universal', 'behavioral'])
  const [result, setResult] = useState<DistillationResult | null>(null)

  const distillation = useRunDistillation()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const input: DistillationRequest = {
      content,
      content_type: contentType,
      context: context || undefined,
      tiers,
    }

    try {
      const res = await distillation.mutateAsync({ canonicalId, input })
      setResult(res)
      onSuccess?.(res)
    } catch (error) {
      console.error('Distillation failed:', error)
    }
  }

  const toggleTier = (tier: 'universal' | 'behavioral') => {
    setTiers((prev) =>
      prev.includes(tier) ? prev.filter((t) => t !== tier) : [...prev, tier]
    )
  }

  return (
    <Card variant="outlined" padding="none" className="overflow-hidden">
      <CardHeader className="bg-warm-50 border-b border-warm-200 p-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Beaker className="w-5 h-5 text-copper-600" />
          Run Distillation
        </CardTitle>
      </CardHeader>

      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-2">
              Content Type
            </label>
            <div className="grid grid-cols-3 gap-2">
              {CONTENT_TYPES.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setContentType(value)}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    contentType === value
                      ? 'border-copper-500 bg-copper-50 text-copper-700'
                      : 'border-warm-200 hover:border-warm-300'
                  }`}
                >
                  <Icon className="w-4 h-4 mb-1" />
                  <span className="text-xs">{label}</span>
                </button>
              ))}
            </div>
          </div>

          <Textarea
            label="Content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={
              contentType === 'questionnaire'
                ? 'Q: How do you approach new challenges?\nA: I prefer to...'
                : 'Enter content to distill...'
            }
            rows={8}
            required
          />

          <Input
            label="Context (optional)"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Any additional context about this content..."
          />

          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-2">
              Signal Tiers
            </label>
            <div className="flex gap-2">
              {(['universal', 'behavioral'] as const).map((tier) => (
                <button
                  key={tier}
                  type="button"
                  onClick={() => toggleTier(tier)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    tiers.includes(tier)
                      ? 'bg-copper-100 text-copper-700'
                      : 'bg-warm-100 text-warm-500'
                  }`}
                >
                  {tier.charAt(0).toUpperCase() + tier.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <Button
            type="submit"
            isLoading={distillation.isPending}
            disabled={!content.trim() || tiers.length === 0}
            fullWidth
          >
            Run Distillation
          </Button>
        </form>

        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 pt-6 border-t border-warm-200"
          >
            <h4 className="font-medium text-midnight-800 mb-3">Results</h4>
            <div className="grid grid-cols-3 gap-4 mb-4 text-center">
              <div className="bg-sage-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-sage-700">{result.signals_extracted}</div>
                <div className="text-xs text-sage-600">Signals</div>
              </div>
              <div className="bg-copper-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-copper-700">
                  {result.preferences_extracted}
                </div>
                <div className="text-xs text-copper-600">Preferences</div>
              </div>
              <div className="bg-warm-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-midnight-700">
                  {result.new_observation_count}
                </div>
                <div className="text-xs text-warm-600">Observations</div>
              </div>
            </div>

            {result.signal_diffs.length > 0 && <SignalDiffViewer diffs={result.signal_diffs} />}
          </motion.div>
        )}
      </CardContent>
    </Card>
  )
}
